import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatCard } from './StatCard';
import { ChevronRight, Clock, MousePointer2, Target, UserCheck, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchDashboardStats, setStatsPeriod } from '../store/dashboardSlice';
import type { TimePeriod } from '../types';

export const StatsSection: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const navigateAction = useNavigate();
  const dispatchAction = useAppDispatch();
  const currentWorkspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const {
    data: statsData,
    period: selectedPeriod,
    loading: isStatsLoading,
  } = useAppSelector((state) => state.dashboard.stats);

  const handleStatsPeriodChange = (newPeriod: TimePeriod) => {
    dispatchAction(setStatsPeriod(newPeriod));
    if (currentWorkspaceId) {
      dispatchAction(fetchDashboardStats({ workspaceId: currentWorkspaceId, period: newPeriod }));
    }
  };

  if (!statsData && !isStatsLoading) {
    return null;
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-medium text-dark leading-6">{t('stats.sectionTitle')}</h2>
        <div className="flex items-center justify-between sm:justify-start gap-4">
          <Select
            value={selectedPeriod}
            onValueChange={(value) => handleStatsPeriodChange(value as TimePeriod)}
          >
            <SelectTrigger className="w-fit min-w-35 bg-white h-9 text-sm rounded-lg border-neutral-100 px-4">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TOTAL">{t('stats.periods.TOTAL')}</SelectItem>
              <SelectItem value="YEAR">{t('stats.periods.YEAR')}</SelectItem>
              <SelectItem value="MONTH">{t('stats.periods.MONTH')}</SelectItem>
              <SelectItem value="WEEK">{t('stats.periods.WEEK')}</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="ghost"
            className="text-dark font-medium flex items-center gap-1 hover:bg-transparent"
            onClick={() => navigateAction('/analytics')}
          >
            {t('stats.seeAll')}
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6 items-stretch">
        <StatCard
          icon={<Users size={20} />}
          value={statsData?.totalRegistrations || 0}
          label={t('stats.registrations.label')}
          subLabel={t('stats.registrations.subLabel')}
        />
        <StatCard
          icon={<UserCheck size={20} />}
          value={statsData?.totalUniqueAttendees || 0}
          label={t('stats.attendees.label')}
          subLabel={t('stats.attendees.subLabel')}
        />
        <StatCard
          icon={<Target size={20} />}
          value={`${statsData?.averageAttendanceRate || 0}%`}
          label=""
          subLabel={t('stats.attendanceRate.subLabel')}
        />
        <StatCard
          icon={<MousePointer2 size={20} />}
          value={statsData?.totalOfferClicks || 0}
          label={t('stats.offerClicks.label')}
          subLabel={t('stats.offerClicks.subLabel')}
        />
        <StatCard
          icon={<Clock size={20} />}
          value={statsData?.totalWatchTimeHours || 0}
          label={t('stats.watchTime.label')}
          subLabel={t('stats.watchTime.subLabel')}
        />
      </div>
    </section>
  );
};
